import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, Select } from 'antd';

import { exampleFor, readToken, tokenOf, transformNamed, transformOptions } from './composerModel';
import {
	APPLY,
	CANCEL,
	EXAMPLE_NONE,
	NO_TRANSFORM,
	NO_TRANSFORMS_FOR_LINK,
	NO_TRANSFORMS_UNKNOWN,
	TRANSFORM_PICK,
	exampleLine,
	transformTitle,
} from './composerCopy';

const NONE = '';

/** The choices of a picked argument, grouped as the formula language groups them (units under their measure). */
function choiceOptions(choices) {
	const groups = new Map();
	const flat = [];
	choices.forEach(choice => {
		const option = { value: choice.value, label: choice.label || choice.value };
		if (!choice.group) flat.push(option);
		else {
			if (!groups.has(choice.group)) groups.set(choice.group, []);
			groups.get(choice.group).push(option);
		}
	});
	return [...flat, ...[...groups.entries()].map(([label, options]) => ({ label, options }))];
}

/**
 * Changes how one pill is written: a transform from the formula language's
 * function list, with the literals it needs, and what it makes of a real item
 * (KPR-708). The functions offered are the language's own typed parameters
 * (`transformOptions`), never a list here. Nothing changes until Apply.
 *
 *   token       the pill being changed
 *   info        what the pill is (`pillType`), or null when the page cannot tell
 *   words       the pill's own words (its label)
 *   preview     the dry run's real items, `[{ title, before }]`
 *   sources     the source card's elements (to read an example's values)
 */
export default function TransformDialog({ open, token, info, words, sources, preview = [], onApply, onCancel }) {
	const pill = useMemo(() => (token ? readToken(token) : null), [token]);
	const groups = useMemo(() => transformOptions(info), [info]);
	const [fn, setFn] = useState(NONE);
	const [args, setArgs] = useState([]);

	useEffect(() => {
		if (!open || !pill) return;
		setFn(pill.fn || NONE);
		setArgs(pill.args || []);
	}, [open, pill]);

	const chosen = fn ? transformNamed(info, fn) : null;
	const params = chosen ? chosen.params : [];
	const working = useMemo(() => (pill && !pill.fixed ? { ...pill, fn: fn || null, args } : null), [pill, fn, args]);

	// The first real item that holds a value for this pill.
	const example = useMemo(() => {
		if (!working) return null;
		const plain = { ...working, fn: null, args: [] };
		for (const item of preview) {
			const after = exampleFor(working, { sources, values: item.before || {} });
			if (after.text === undefined) continue;
			const before = exampleFor(plain, { sources, values: item.before || {} });
			return { title: item.title, before: before.text ?? '', after: after.text };
		}
		return null;
	}, [working, preview, sources]);

	const options = [
		{ value: NONE, label: NO_TRANSFORM },
		...groups.map(group => ({
			label: group.group,
			options: group.options.map(option => ({ value: option.name, label: option.label, title: option.description })),
		})),
	];

	const setArg = (index, value) => setArgs(prev => {
		const next = [...prev];
		while (next.length <= index) next.push('');
		next[index] = value;
		return next;
	});

	const pickFn = next => {
		setFn(next);
		setArgs([]);
	};

	// A required literal still empty blocks Apply: the formula would be refused.
	const missing = params.some((param, i) => !param.optional && !String(args[i] ?? '').trim());

	const apply = () => {
		if (!pill || missing) return;
		onApply(tokenOf({ base: pill.base, fn: fn || null, args: fn ? args.slice(0, params.length) : [] }));
	};

	const noneAvailable = !groups.length;

	return (
		<Modal
			open={open}
			title={transformTitle(words || '')}
			onCancel={onCancel}
			onOk={apply}
			okText={APPLY}
			cancelText={CANCEL}
			okButtonProps={{ disabled: missing || noneAvailable }}
			destroyOnClose
			width={480}
			style={{ maxWidth: 'calc(100vw - 32px)' }}
			className="change-card__dialog"
		>
			{noneAvailable ? (
				<p className="change-card__note">{info ? NO_TRANSFORMS_FOR_LINK : NO_TRANSFORMS_UNKNOWN}</p>
			) : (
				<div className="change-card__transform">
					<label className="change-card__transform-field">
						<span>{TRANSFORM_PICK}</span>
						<Select
							showSearch
							optionFilterProp="label"
							value={fn}
							options={options}
							onChange={pickFn}
							aria-label={`${TRANSFORM_PICK} for ${words || 'this value'}`}
							popupMatchSelectWidth={false}
						/>
					</label>
					{chosen ? <p className="change-card__note">{chosen.description}</p> : null}
					{params.map((param, i) => (
						<label key={param.name} className="change-card__transform-field">
							<span>{param.name}{param.optional ? ' (optional)' : ''}</span>
							{param.choices ? (
								<Select
									showSearch
									optionFilterProp="label"
									value={args[i] || undefined}
									options={choiceOptions(param.choices)}
									onChange={value => setArg(i, value)}
									aria-label={param.name}
									popupMatchSelectWidth={false}
									allowClear={!!param.optional}
								/>
							) : (
								<Input
									value={args[i] ?? ''}
									onChange={event => setArg(i, event.target.value)}
									inputMode={param.kind === 'number' ? 'decimal' : 'text'}
									aria-label={param.name}
								/>
							)}
						</label>
					))}
					<div className="change-card__example" aria-live="polite">
						{example
							? <><span className="change-card__example-title">{example.title || 'Example'}</span><span>{exampleLine(example.before || '—', example.after)}</span></>
							: <span className="change-card__muted">{EXAMPLE_NONE}</span>}
					</div>
				</div>
			)}
		</Modal>
	);
}
